import React, { useMemo, useState } from 'react';
import { Box, Typography, Chip, Alert, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { UI_TEXT } from './ExitPlanPage.constants';

export const PlanProductsDisplay = ({
                                        products,
                                        selectedProductIds,
                                        isViewMode,
                                        onRemoveProduct
                                    }) => {
    const [showAll, setShowAll] = useState(false);

    const filteredProducts = useMemo(() => {
        if (!Array.isArray(products) || !Array.isArray(selectedProductIds) || selectedProductIds.length === 0) {
            return [];
        }
        return products.filter((p) => selectedProductIds.includes(p.productId));
    }, [products, selectedProductIds]);

    const hasProducts = filteredProducts.length > 0;
    const maxVisible = 15;
    const hasOverflow = filteredProducts.length > maxVisible;
    const visibleProducts = showAll ? filteredProducts : filteredProducts.slice(0, maxVisible);
    const hiddenCount = hasOverflow ? filteredProducts.length - maxVisible : 0;
    const allowRemoval = !isViewMode && typeof onRemoveProduct === 'function';
    const shouldShowEmptyState = !hasProducts;

    const handleToggleView = () => setShowAll((prev) => !prev);

    return (
        <Box sx={{ mb: 1, mt: 0, p: 0, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {UI_TEXT.PRODUCTS_IN_PLAN}
                </Typography>
                {hasProducts && (
                    <Typography variant="caption" color="text.secondary">
                        {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
                    </Typography>
                )}
            </Box>
            {hasProducts ? (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {visibleProducts.map((product) => (
                            <Chip
                                key={product.productId}
                                label={product.productName}
                                size="small"
                                color="primary"
                                sx={{ fontWeight: 500, cursor: allowRemoval ? 'pointer' : 'default' }}
                                onDelete={allowRemoval ? () => onRemoveProduct(product.productId) : undefined}
                                deleteIcon={allowRemoval ? <CloseIcon /> : undefined}
                            />
                        ))}
                    </Box>
                    {hasOverflow && (
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleToggleView}
                            sx={{ mt: 1, textTransform: 'none', alignSelf: 'flex-start', px: 0 }}
                        >
                            {showAll ? 'Show fewer products' : `Show ${hiddenCount} more`}
                        </Button>
                    )}
                </>
            ) : (
                shouldShowEmptyState && (
                    isViewMode ? (
                        <Typography variant="body2" color="text.secondary">
                            {UI_TEXT.NO_PRODUCTS_SELECTED}
                        </Typography>
                    ) : (
                        <Alert
                            severity="warning"
                            sx={{
                                py: 0.2,
                                fontSize: '0.9rem',
                                minHeight: 'auto',
                                lineHeight: 1
                            }}
                        >
                            {UI_TEXT.SELECT_PRODUCTS_WARNING}
                        </Alert>
                    )
                )
            )}
        </Box>
    );
};
